/**
 * Wrapper button for the design library button to pass our router specific link
 */

import { type ExoticComponent, forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { type ButtonProps, type ButtonVariant, Button as DesignLibraryButton } from '@mweb/zappos-ui/Button';

import { cn } from 'helpers/classnames';

const rebrandButtonClasses: Record<ButtonVariant, string> = {
  filled: cn(
    'lumon:bg-gray-900',
    'lumon:border-gray-900 lumon:border lumon:border-solid lumon:rounded-sm',
    `lumon:font-sans lumon:font-medium lumon:leading-[1.2] lumon:text-sm lumon:text-gray-0 lumon:tracking-[0.05em] lumon:uppercase`,
    'lumon:active:bg-gray-500',
    'lumon:disabled:bg-gray-200 lumon:disabled:border-[transparent] lumon:disabled:text-gray-400',
    'lumon:hover:bg-gray-700'
  ),
  outlined: cn(
    'lumon:bg-gray-0',
    'lumon:border-gray-300 lumon:border lumon:border-solid lumon:rounded-sm',
    `lumon:font-sans lumon:font-medium lumon:leading-[1.2] lumon:text-sm lumon:text-gray-900 lumon:tracking-[0.05em] lumon:uppercase`,
    'lumon:active:bg-gray-100',
    'lumon:disabled:bg-gray-50 lumon:disabled:text-gray-400',
    'lumon:hover:bg-gray-50'
  ),
  outlinedReversed: cn(
    'lumon:rounded-sm',
    `lumon:font-sans lumon:font-medium lumon:leading-[1.2] lumon:text-sm lumon:tracking-[0.05em] lumon:uppercase`,
    'lumon:hover:border-gray-0 lumon:hover:text-gray-0'
  ),
  text: cn(`lumon:font-sans`, 'lumon:hover:text-gray-900'),
  textReversed: cn(`lumon:font-sans`, 'lumon:hover:text-gray-0')
} as const;

type ButtonWrapperProps = Omit<ButtonProps, 'linkComponent' | 'linkProps'>;

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonWrapperProps>(({ className, href, variant, ...rest }, ref) => (
  <DesignLibraryButton
    className={cn(rebrandButtonClasses[variant ?? 'filled'], className)}
    {...rest}
    href={href}
    linkComponent={Link as ExoticComponent}
    linkProps={{ to: href }}
    ref={ref}
    variant={variant}
  />
));

Button.displayName = 'Button';

export default Button;
